import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client from "./api";

const AuthCtx = createContext(null);

const DEFAULT_BRAND = { title: "Register", subtitle: "" };
const DEFAULT_PREFS = { auto_signout_min: 5 };

export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState({ loading: true, setupRequired: false, authed: false });
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [wallpaper, setWallpaper] = useState({ url: "" });
  const [me, setMe] = useState(null);

  const reloadMe = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setMe(data);
    } catch {
      setMe(null);
    }
  }, []);

  const reloadBrand = useCallback(async () => {
    try {
      const { data } = await client.get("/brand");
      setBrand({ title: data.title || "", subtitle: data.subtitle || "" });
    } catch {
      setBrand(DEFAULT_BRAND);
    }
  }, []);

  const reloadPrefs = useCallback(async () => {
    try {
      const { data } = await client.get("/prefs");
      setPrefs({ auto_signout_min: data.auto_signout_min || 5 });
    } catch {
      setPrefs(DEFAULT_PREFS);
    }
  }, []);

  const reloadWallpaper = useCallback(async () => {
    try {
      const { data } = await client.get("/wallpaper");
      setWallpaper({ url: data.url || "" });
    } catch {
      setWallpaper({ url: "" });
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/status");
      const setupRequired = !!data.setup_required;
      const token = localStorage.getItem("owner_token");
      let authed = false;
      if (token && !setupRequired) {
        try {
          const { data } = await client.get("/auth/me");
          setMe(data);
          authed = true;
        } catch {
          localStorage.removeItem("owner_token");
          setMe(null);
        }
      } else {
        setMe(null);
      }
      setStatus({ loading: false, setupRequired, authed });
    } catch {
      setStatus({ loading: false, setupRequired: false, authed: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    reloadBrand();
    reloadPrefs();
    reloadWallpaper();
  }, [refresh, reloadBrand, reloadPrefs, reloadWallpaper]);

  const login = async (pin) => {
    const { data } = await client.post("/auth/login", { pin });
    localStorage.setItem("owner_token", data.token);
    setStatus((s) => ({ ...s, authed: true, setupRequired: false }));
    await reloadMe();
  };

  const setupOwner = async (pin) => {
    const { data } = await client.post("/auth/setup", { pin });
    localStorage.setItem("owner_token", data.token);
    setStatus((s) => ({ ...s, authed: true, setupRequired: false }));
    await reloadMe();
  };

  const logout = () => {
    localStorage.removeItem("owner_token");
    setStatus((s) => ({ ...s, authed: false }));
    setMe(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        ...status,
        brand,
        prefs,
        wallpaper,
        me,
        login,
        setupOwner,
        logout,
        refresh,
        reloadBrand,
        reloadPrefs,
        reloadWallpaper,
        reloadMe,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
